<div class="sheets-editor">
  <!-- Bound to an item, the expanded header above shows the title and, since AM-78, the save
       state, so this row is only for the unbound /sheets-spike page. -->
  @if (!boundToItem()) {
    <header class="sheets-editor__head">
      <h1 class="sheets-editor__title">{{ docTitle() }}</h1>
      <span class="sheets-editor__state" [attr.data-state]="saveState()">{{ saveLabel() }}</span>
      <!-- Bound, "Download and share" is in the expanded header (the prototype's title bar); here, beside this title. -->
      <span class="tb__menuhost sheets-editor__share">
        <button #downloadButton class="tb" [class.tb--active]="menuOpen()" [title]="S['Download and share']" (click)="toggleDownload(downloadButton)" [attr.aria-label]="S['Download and share']">
          <app-icon name="share" size="16" />
        </button>
        @if (menuOpen()) {
          <app-download-menu [share]="false" [style.--dl-top.px]="downloadAt().top" [style.--dl-right.px]="downloadAt().right" (action)="onDownload($event)" />
        }
      </span>
    </header>
  }

  <!-- SM-SAVE banners (§5.4), exact wording. -->
  @if (saveState() === 'error_blocked') {
    <div class="sheets-editor__banner" role="alert">
      <span>{{ fill("Changes can't be saved. {reason}", { reason: blockedReason() ?? '' }) }}</span>
      <button class="sheets-editor__banner-action" (click)="retrySave()" [attr.aria-label]="S['Try again']">{{ S['Try again'] }}</button>
    </div>
  }
  @if (saveState() === 'read_only') {
    <div class="sheets-editor__banner" role="status">
      <span>{{ S['This workbook is in Trash. Restore it to edit.'] }}</span>
      <button class="sheets-editor__banner-action" (click)="restoreFromTrash()" [attr.aria-label]="S.Restore">{{ S.Restore }}</button>
    </div>
  }

  <!-- The Stitch toolbar (§11.6.3), built from toolbar-spec.ts: the pack's 31 controls in its order,
       with its tooltips. The engine's own ribbon stays off (AM-11).
       NFR-7: a toolbar, labelled, whose controls Left/Right move between. -->
  <!-- One toolbar item, drawn in the row and, when the row is too narrow, in "More tools". -->
  <ng-template #toolbarItem let-c>
      @if (!c) {
        <span class="tb__sep"></span>
      } @else if (c.kind === 'select') {
        <span class="tb tb--font">
          <select [title]="tip(c)" [attr.aria-label]="tip(c)" (change)="run(c.main!, $any($event.target).value)">
            @for (f of fontFamilies(); track f) {
              <option [value]="f" [selected]="f === format().fontFamily">{{ f }}</option>
            }
          </select>
        </span>
      } @else if (c.kind === 'button') {
        <button
          class="tb"
          [class.tb--active]="isActive(c.activeWhen)"
          [attr.aria-pressed]="c.activeWhen ? isActive(c.activeWhen) : null"
          [disabled]="isDisabled(c)"
          [title]="tip(c)"
          [attr.aria-label]="tip(c)"
          (click)="runMain(c)"
          (dblclick)="c.dblclick && run(c.dblclick)"
        >
          @if (c.icon) {
            <app-icon [name]="c.icon" size="16" />
          } @else {
            <span class="tb__glyph" [attr.data-glyph]="c.id">{{ c.glyph }}</span>
          }
        </button>
      } @else {
        <span class="tb__menuhost" (keydown)="menuKey($event, opener)">
          @if (c.main) {
            <!-- A split button: the main part runs, the chevron opens the menu. -->
            <button class="tb" [class.tb--active]="isActive(c.activeWhen)" [title]="tip(c)" [attr.aria-label]="tip(c)" (click)="runMain(c)">
              @if (c.icon) {
                <app-icon [name]="c.icon" size="16" />
              } @else {
                <span class="tb__glyph">{{ c.glyph }}</span>
              }
              @if (c.id === 'text-colour' || c.id === 'fill-colour') {
                <span class="tb__swatchbar" [style.background]="c.id === 'text-colour' ? textColour() : fillColour()"></span>
              }
            </button>
          }
          <button
            #opener
            class="tb tb--menu"
            [class.tb--split]="!!c.main"
            [class.tb--active]="!c.main && isActive(c.activeWhen)"
            [title]="tip(c)"
            [attr.aria-label]="tip(c)"
            aria-haspopup="menu"
            [attr.aria-expanded]="openMenu() === c.id"
            (click)="toggleMenu(c, opener)"
          >
            @if (!c.main) {
              @switch (c.id) {
                @case ('font-size') {
                  <span class="tb__value">{{ format().fontSize }}</span>
                }
                @case ('number-format') {
                  <span class="tb__value tb__value--wide">{{ numberFormatName() }}</span>
                }
                @default {
                  @if (iconFor(c); as icon) {
                    <app-icon [name]="icon" size="16" />
                  } @else {
                    <span class="tb__glyph">{{ c.glyph }}</span>
                  }
                }
              }
            }
            <app-icon name="chevrondown" size="12" />
          </button>
          @if (openMenu() === c.id) {
            <div class="tb__menu" role="menu" [attr.aria-label]="tip(c)" [style.left.px]="menuAt().left" [style.top.px]="menuAt().top">
              @for (e of c.menu ?? []; track $index) {
                @if (entryRun(e); as r) {
                  <button
                    class="tb__item"
                    [attr.role]="'checkedWhen' in e ? 'menuitemcheckbox' : 'menuitem'"
                    [attr.aria-checked]="'checkedWhen' in e ? entryChecked(e) : null"
                    [attr.aria-label]="$any(e).label"
                    [disabled]="entryDisabled(e)"
                    (click)="run(r)"
                  >
                    <span class="tb__check">{{ entryChecked(e) ? '✓' : '' }}</span>
                    <span class="tb__label">{{ $any(e).label }}</span>
                    @if ($any(e).shortcut) {
                      <span class="tb__hint">{{ keys($any(e).shortcut) }}</span>
                    }
                  </button>
                } @else if ('divider' in e) {
                  <hr class="tb__divider" />
                } @else if ('lineStyle' in e) {
                  <label class="tb__row">
                    <span>{{ S['Line style'] }}</span>
                    <select [attr.aria-label]="S['Line style']" (change)="borderStyle.set($any($event.target).value)">
                      @for (b of borderStyles; track b.value) {
                        <option [value]="b.value" [selected]="b.value === borderStyle()">{{ b.label }}</option>
                      }
                    </select>
                  </label>
                } @else if ('picker' in e) {
                  <!-- §11.7: 4 rows of 10. -->
                  <div class="tb__palette" role="group" [attr.aria-label]="e.picker === 'border' ? S['Border colour'] : tip(c)">
                    @for (colour of palette; track colour) {
                      <button
                        class="tb__colour"
                        [class.tb__colour--on]="colour === (e.picker === 'text' ? textColour() : e.picker === 'fill' ? fillColour() : borderColour())"
                        [style.background]="colour"
                        [attr.aria-label]="colour"
                        (click)="pickColour(e.picker, colour)"
                      ></button>
                    }
                  </div>
                }
              }
            </div>
          }
        </span>
      }
  </ng-template>

  <div class="toolbar" role="toolbar" [attr.aria-label]="S['Sheet formatting tools']" (keydown)="toolbarKey($event)" #bar>
    <!-- Every item stays in the row, so each fit measures them all; one that does not fit is kept
         out of the flow and out of reach (and shown in "More tools" instead). -->
    @for (c of toolbar; track $index) {
      <span class="tb__slot" [class.tb__slot--out]="$index >= shownCount()" [attr.data-slot]="$index" [attr.aria-hidden]="$index >= shownCount() ? 'true' : null">
        <ng-container *ngTemplateOutlet="toolbarItem; context: { $implicit: c }" />
      </span>
    }
    <!-- What does not fit goes here, in order: the row stays one row (§11.6.3). -->
    @if (shownCount() < toolbar.length) {
      <span class="tb__menuhost tb__overflowhost" (keydown.escape)="overflowOpen.set(false); overflowButton.focus()">
        <button
          #overflowButton
          class="tb tb--menu"
          [class.tb--active]="overflowOpen()"
          [title]="S['More tools']"
          [attr.aria-label]="S['More tools']"
          aria-haspopup="true"
          [attr.aria-expanded]="overflowOpen()"
          (click)="toggleOverflow(overflowButton)"
        >
          <app-icon name="dots" size="16" />
        </button>
        @if (overflowOpen()) {
          <div class="tb__overflow" role="group" [attr.aria-label]="S['More tools']" [style.right.px]="overflowAt().right" [style.top.px]="overflowAt().top">
            @for (c of toolbar; track $index) {
              @if ($index >= shownCount() && c) {
                <ng-container *ngTemplateOutlet="toolbarItem; context: { $implicit: c }" />
              }
            }
          </div>
        }
      </span>
    }
  </div>
  @if (pdfOpen()) {
    <app-pdf-dialog
      [sheets]="pdfSheets()"
      [activeSheetId]="pdfActiveSheet()"
      [ready]="!loading() && !mountFailed()"
      [busy]="pdfBusy()"
      (confirm)="runPdf($event)"
      (cancelled)="pdfOpen.set(false)"
    />
  }
  @if (shortcutsOpen()) {
    <app-shortcuts-dialog (closed)="shortcutsOpen.set(false)" />
  }
  @if (importOpen()) {
    <app-import-dialog [busy]="importBusy()" (chosen)="openFile($event)" (cancelled)="importOpen.set(false)" />
  }
  <div class="sheets-editor__row">
    <div class="sheets-editor__grid">
      @if (loading()) {
        <div class="sheets-editor__loading">{{ S['Loading the spreadsheet…'] }}</div>
      } @else if (mountFailed()) {
        <div class="sheets-editor__loading">
          {{ S['The spreadsheet editor failed to load. Reload the page to try again.'] }}
        </div>
      }
      <div class="stitch-sheets-host" #host></div>
    </div>
    @if (validationOpen()) {
      <app-validation-panel
        [initialRange]="panelRange()"
        [existing]="validationExisting()"
        (done)="applyValidation($event)"
        (cancelled)="validationOpen.set(false)"
        (remove)="removeValidation($event)"
      />
    }
    @if (splitOpen()) {
      <app-split-panel [range]="panelRange()" (split)="applySplit($event)" (dismiss)="splitOpen.set(false)" />
    }
    @if (cfOpen()) {
      <app-cf-panel
        [sheetRules]="cfRules()"
        [initialRange]="panelRange()"
        [preset]="cfPreset()"
        (save)="applyCf($event)"
        (removeRule)="removeCf($event)"
        (cancelled)="cfOpen.set(false)"
      />
    }
  </div>
  @if (stitchyToast(); as t) {
    <div class="sheets-editor__toast sheets-editor__toast--stitchy" role="status">
      <span>{{ t.text }}</span>
      <button type="button" class="sheets-editor__toast-action" (click)="undoStitchy()" [attr.aria-label]="S.Undo">{{ S.Undo }}</button>
      <button type="button" class="sheets-editor__toast-action" (click)="reviewStitchy()" [attr.aria-label]="S.Review">{{ S.Review }}</button>
    </div>
  }
  @if (toast(); as t) {
    <div class="sheets-editor__toast" [class.sheets-editor__toast--above]="stitchyToast()" [class.sheets-editor__toast--stitchy]="toastVersions() !== null">
      @if (toastVersions() !== null) {
        <span>{{ t }}</span>
        <button type="button" class="sheets-editor__toast-action" (click)="viewVersions()" [attr.aria-label]="S['View versions']">{{ S['View versions'] }}</button>
      } @else {
        {{ t }}
      }
    </div>
  }

  <!-- §5.6. Deliberately no Escape handler and no backdrop click: the dialog cannot be dismissed
       without choosing, because closing it would leave this tab saving onto a revision it has not seen. -->
  @if (conflictOpen()) {
    <div class="sheets-editor__scrim">
      <div class="sheets-editor__dialog" role="alertdialog" appDialogFocus aria-labelledby="sheets-conflict-title">
        <h2 id="sheets-conflict-title" class="sheets-editor__dialog-title">{{ S['This workbook changed in another tab'] }}</h2>
        <p class="sheets-editor__dialog-body">{{ S['Choose which version to keep. Keep your changes, and the other version stays in version history. Load the other version, and your changes in this tab are lost.'] }}</p>
        <div class="sheets-editor__dialog-actions">
          <button class="sheets-editor__dialog-secondary" (click)="loadOther()" [attr.aria-label]="S['Load the other version']">{{ S['Load the other version'] }}</button>
          <button class="sheets-editor__dialog-primary" (click)="keepMine()" [attr.aria-label]="S['Keep my changes']">{{ S['Keep my changes'] }}</button>
        </div>
      </div>
    </div>
  }
</div>
